import axios from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { Buffer } from 'buffer';
import Dexie from 'dexie';
import { analyticsService } from '../api/analytics';
import { logout } from '../api/auth';
import { syncedDBModel } from '../models/synced';
import { userDB } from '../models/user';
import { EventNames } from '../types/analytics';
import { SyncRecordStatus } from '../types/sync-state';
import { userCache } from './user-cache';
import { Region } from '@taleemabad/dsm/components';
import { setServerPullFlag } from '../models/base-model';

/**
 * Ideally this should be inside the shared module
 * however since its using the user model we are
 * keeping it in the db module.
 */

export const apiService = applyCaseMiddleware(
  axios.create({
    timeout: 180000,
  }),
  {
    caseOptions: {
      stripRegexp: /[^A-Z0-9[\]$]+/gi,
    },
  },
);

apiService.interceptors.request.use(async (config) => {
  config.baseURL =
    (await userCache.getBaseUrl()) ?? process.env.NX_PUBLIC_BASE_URI;
  const token = userCache.getTokenFromCache();
  // This is excluded since the Google Translate API does not require a bearer token for API calls.
  // If we pass token then it returns unauthorized error in response

  const exludedUrls: string[] = [
    process.env.NX_PUBLIC_TRANSLATE_API_URL!,
    process.env.NX_PUBLIC_TENANT_URL!,
  ];
  if (
    token &&
    token.access &&
    config.url &&
    config.url !== '/users/token/refresh/' &&
    !exludedUrls.some((excluded) => config.url?.includes(excluded))
  ) {
    config.headers.setAuthorization(`Bearer ${token.access}`);
  }
  return config;
});

apiService.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    // this block handles the tracking of backend errors for analytics, there are two main conditions under which we track the error:
    // 1. when the error status is 401 and the request has already been retried.
    //    The assumption here is that a 401 error is typically due to token expiry,
    //    in such cases, we attempt a retry, if the retry fails, it indicates
    //    an unresolved authentication issue, and we track this as an error event.
    //    if the retry succeeds, we do not track it as an error.

    // if user token is not available that means the user is not logged in yet and hence we should not attempt any retries on 401
    const isUserCacheAvailable = userCache.getTokenFromCache();
    if (
      error.response?.status === 401 &&
      isUserCacheAvailable &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      return updateToken().then((data) => {
        if (data && data.access) {
          error.config.headers['Authorization'] = `Bearer ${data.access}`;
          return axios.request(originalRequest);
        }
      });
    }
    return Promise.reject(error);
  },
);

export const updateToken = async () => {
  try {
    const token = userCache.getTokenFromCache();
    if (!token || !token.refresh) {
      throw new Error('Refresh token not found.');
    }
    const response = await apiService.post('/users/token/refresh/', {
      refresh: token.refresh,
    });
    userDB.setToken(response.data);
    return response.data;
  } catch (error) {
    // if the refresh endpoint fails,
    // then log the user out, reload to
    // force exit the application
    console.log('Unable to refresh token due to', error);
    await logout();
    window.location.reload();
  }
};

export const getListOfTenants = async (): Promise<Region[]> => {
  const response = await apiService.get(
    `${process.env.NX_PUBLIC_TENANT_URL}/${process.env.NX_PUBLIC_ENV}.json?c=${Date.now()}`,
  );
  return response.data;
};
export const getTenantByName = async (url: string) => {
  const listOfTenants = await getListOfTenants();
  return listOfTenants.find((region) => region.baseUrl === url);
};

const downloadImage = async (url: string) => {
  return axios
    .get(url, {
      responseType: 'arraybuffer',
      // https://stackoverflow.com/questions/76669136/axios-get-from-s3-no-access-control-allow-origin
      headers: { 'Cache-Control': 'no-cache' },
    })
    .then((response) =>
      Buffer.from(response.data, 'binary').toString('base64'),
    );
};

/**
 * Maximum number of thumbnail downloads kept in flight at once.
 *
 * Each in-flight axios call holds an ArrayBuffer plus the resulting base64
 * string in V8 memory until the calling code releases them. On low-end Android
 * devices (256 MB WebView heap), letting the entire course list (often 100+
 * thumbnails) download in parallel pushed peak heap high enough to crash the
 * WebView when stacked with PDF/DOCX writes and Capgo bundle downloads.
 *
 * Capping at 4 keeps peak thumbnail heap bounded at roughly
 * `4 × ~200 KB = ~800 KB` regardless of input size.
 */
export const THUMBNAIL_DOWNLOAD_CONCURRENCY = 4;

export const fetchImageProxyArrayBuffer = async (
  imageUrl: string,
): Promise<{ data: ArrayBuffer; contentType: string } | null> => {
  const baseUrl =
    (await userCache.getBaseUrl()) ?? process.env.NX_PUBLIC_BASE_URI ?? '';
  const token = userCache.getTokenFromCache();
  const normalizedBaseUrl = baseUrl.endsWith('/')
    ? baseUrl.slice(0, -1)
    : baseUrl;
  const proxyUrl = `${normalizedBaseUrl}/api/v1/ug-eg/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
  const headers: Record<string, string> = {};
  if (token?.access) {
    headers['Authorization'] = `Bearer ${token.access}`;
  }
  const response = await axios.get<ArrayBuffer>(proxyUrl, {
    responseType: 'arraybuffer',
    headers,
  });
  if (!response.data || response.data.byteLength === 0) return null;
  return {
    data: response.data,
    contentType: response.headers['content-type'] || 'image/jpeg',
  };
};

export const bulkImageDownloadFromUrlToBase64 = async (
  urls: string[],
): Promise<PromiseSettledResult<{ url: string; base64: string }>[]> => {
  const results: PromiseSettledResult<{ url: string; base64: string }>[] = [];

  for (let i = 0; i < urls.length; i += THUMBNAIL_DOWNLOAD_CONCURRENCY) {
    const batch = urls.slice(i, i + THUMBNAIL_DOWNLOAD_CONCURRENCY);
    const batchResults = await Promise.allSettled(
      batch.map(async (url) => {
        try {
          const base64 = await downloadImage(url);
          return { url, base64 };
        } catch {
          // Throwing carries the URL through to the caller's reason.message
          // so the rejected entry can be matched back to its source URL.
          throw new Error(url);
        }
      }),
    );
    results.push(...batchResults);
  }

  return results;
};

/**
 * Lazy-load helper for UI components that want to fetch a single thumbnail
 * when its card scrolls into view, instead of downloading every thumbnail
 * up-front during sync.
 *
 * Resolves to a base64 string on success, or `null` on any failure — callers
 * are expected to render a placeholder when the result is null.
 */
export const downloadThumbnailOnDemand = async (
  url: string,
): Promise<string | null> => {
  try {
    return await downloadImage(url);
  } catch {
    return null;
  }
};
const deleteDatabase = async (dbName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(dbName);

    request.onsuccess = function () {
      resolve();
    };

    request.onerror = function () {
      console.error(`Error deleting database ${dbName}`);
      reject(request.error);
    };

    request.onblocked = function () {
      console.warn(
        `Database ${dbName} deletion is blocked. Close open connections.`,
      );
    };
  });
};
export const resetDB = async () => {
  await syncedDBModel.resetSyncRecord();
  return await Promise.all([
    deleteDatabase('userdb'),
    deleteDatabase('taleemabad'),
    deleteDatabase('analyticsdb'),
    deleteDatabase('notifications'),
    deleteDatabase('lessonplandb'),
    deleteDatabase('examgeneratordb'),
    deleteDatabase('flnAssessmentdb'),
    deleteDatabase('teachertrainingdb'),
    deleteDatabase('studentdb'),
    deleteDatabase('notificationdb'),
    deleteDatabase('coachingdb'),
    deleteDatabase('CoreLessonPlanDB'),
    deleteDatabase('UserGeneratedExamDB'),
  ]);
};

/**
 * Asynchronously extracts data from a server response and updates the local database accordingly.
 *
 * @param {any[]} models - An array of model names to be processed.
 * @param {any} response - The server response containing the data to be extracted.
 * @param {Dexie} db - The Dexie instance representing the local database.
 *
 * @returns {Promise<any[]>} - A Promise that resolves with an array of results from the database operations.
 *
 * The function performs the following steps:
 * 1. For each model in the provided array, it checks if the model exists in the server response.
 * 2. If the model does not exist in the response, it resolves a Promise with true.
 * 3. If the model does exist, it maps over the keys 'created', 'updated', and 'deleted'.
 * 4. For each key, it extracts the corresponding data from the server response.
 * 5. If there is no data for the key, it resolves a Promise with true.
 * 6. If there is data and the key is 'deleted', it modifies the corresponding records in the database to mark them as deleted.
 * 7. If the key is 'updated', it updates the corresponding records in the database with the new data.
 * 8. If the key is 'created', it adds the new records to the database.
 * 9. It returns a Promise that resolves when all the database operations have completed.
 *
 * Note: This function assumes that the model names in the server response match the table names in the Dexie database.
 */

export function serializeKey(r: any, keyPath: string | string[]): string | number {
  if (Array.isArray(keyPath)) return keyPath.map(k => r[k]).join('\x00');
  return r[keyPath];
}

function keyTuple(r: any, keyPath: string | string[]): any {
  if (Array.isArray(keyPath)) return keyPath.map(k => r[k]);
  return r[keyPath];
}

export const extractSyncResponse = async (
  models: any,
  response: any,
  db: Dexie,
): Promise<any[]> => {
  setServerPullFlag(true);
  try {
    return await Promise.all(
      models.flatMap((model: string) => {
        const modelExists = response[model] !== undefined;
        if (!modelExists) {
          return Promise.resolve(true);
        }
        return ['created', 'updated', 'deleted'].map(async (key) => {
          const modelData = response[model][key]['data'];
          if (!modelData.length) return Promise.resolve(true);
          // @ts-expect-error
          const primaryKey = db[model].schema.primKey.keyPath ?? 'id'; // Default to 'id' if primary key is not  uuid

          if (key === SyncRecordStatus.Deleted) {
            if (Array.isArray(primaryKey)) {
              // Compound primary key (e.g. [profileId, id] for courses/trainings/grandQuizzes/levels).
              // Build [profileId, id] tuples and use the indexed primary-key lookup — avoids a full
              // table scan and the string-comparison mismatch risk in the old filter() path.
              const profileId = response.profileId ?? '';
              if (!profileId) {
                console.warn('[extractSyncResponse] compound delete skipped — profileId is empty', model);
                return Promise.resolve(0);
              }
              const compoundKeys = modelData.map((id: any) => [profileId, Number(id)]);
              // @ts-expect-error - compound anyOf: [[profileId, id], ...] tuples via primary key
              return db[model]
                .where(primaryKey)
                .anyOf(compoundKeys)
                .modify({ syncStatus: SyncRecordStatus.Deleted });
            }
            // @ts-expect-error - we need to ensure the model name in response and db is the same.
            return db[model]
              .where(primaryKey)
              .anyOf(modelData.map(Number))
              .modify({ syncStatus: SyncRecordStatus.Deleted });
          }

          if (
            key === SyncRecordStatus.Created ||
            key === SyncRecordStatus.Updated
          ) {
            // Force-stamp syncStatus: Synced on every incoming record. This is
            // load-bearing: when a previously-Deleted row is re-activated by the
            // server, the bulkPut diff will include syncStatus (Deleted→Synced),
            // so updateHook's server-pull delete bypass does NOT fire and the
            // Deleted→Synced transition is allowed correctly.
            const syncedData = modelData.map((item: any) => ({
              ...item,
              syncStatus: SyncRecordStatus.Synced,
            }));
            // Skip rows that are locally Deleted (pending push-sync or already
            // pushed). Without this guard a pull-sync can resurrect a profile
            // the user just cancelled — particularly when resetLastSyncedAt()
            // fires after a profile-list change and the next pull sends
            // last_pulled_at=null, returning an empty deleted bucket (BUG-005).
            // @ts-expect-error - we need to ensure the model name in response and db is the same.
            const existingRows = await db[model]
              .where(primaryKey)
              .anyOf(syncedData.map((r: any) => keyTuple(r, primaryKey)))
              .toArray();
            // For compound-key tables (courses, trainings, levels, grandQuizzes)
            // users never initiate deletes — only the server does. When the server
            // re-activates one of these records it sends it in `updated`, so we
            // must NOT skip it here. The BUG-005 guard only applies to simple-key
            // tables where users can delete locally (e.g. teacherProfiles).
            const locallyDeleted = Array.isArray(primaryKey)
              ? new Set<string>()
              : new Set(
                  existingRows
                    .filter((r: any) => r.syncStatus === SyncRecordStatus.Deleted)
                    .map((r: any) => serializeKey(r, primaryKey)),
                );
            const safeData = syncedData.filter(
              (r: any) => !locallyDeleted.has(serializeKey(r, primaryKey)),
            );
            if (!safeData.length) return true;
            // @ts-expect-error - we need to ensure the model name in response and db is the same.
            return db[model].bulkPut(safeData);
          }
        });
      }),
    );
  } finally {
    setServerPullFlag(false);
  }
};
